import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { isNil } from 'lodash-es';
import { V2LeanEnergyFlowAnalysisPieChartProductionBasePostResponse } from '@maxtropy/device-customer-apis-v2';

interface Props {
  data: V2LeanEnergyFlowAnalysisPieChartProductionBasePostResponse['list'];
}

const legendColors = [
  {
    '0%': '#8AF5FF',
    '100%': '#52E7FF',
  },
  {
    '0%': '#FF7DB2',
    '100%': '#FF477B',
  },
  {
    '0%': '#FFE77D',
    '100%': '#FFCB47',
  },
  {
    '0%': '#A87DFF',
    '100%': '#6F47FF',
  },
  {
    '0%': '#32F0C2',
    '100%': '#16DD8E',
  },
  {
    '0%': '#8AF5FF',
    '100%': '#52E7FF',
  },
  {
    '0%': '#FF7DB2',
    '100%': '#FF477B',
  },
  {
    '0%': '#FFE77D',
    '100%': '#FFCB47',
  },
  {
    '0%': '#A87DFF',
    '100%': '#6F47FF',
  },
  {
    '0%': '#32F0C2',
    '100%': '#16DD8E',
  },
  {
    '0%': '#8AF5FF',
    '100%': '#52E7FF',
  },
  {
    '0%': '#FF7DB2',
    '100%': '#FF477B',
  },
  {
    '0%': '#FFE77D',
    '100%': '#FFCB47',
  },
  {
    '0%': '#A87DFF',
    '100%': '#6F47FF',
  },
  {
    '0%': '#32F0C2',
    '100%': '#16DD8E',
  },
];

const PieCharts: React.FC<Props> = ({ data }) => {
  const theme = useTheme();

  const getChartOption = () => {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        position: 'top',
        borderColor: 'transparent',
        textStyle: {
          fontSize: 12,
          color: 'var(--mx-text-secondary-color)',
        },
        formatter: (e: any) => {
          // const item = data?.find(i => i.energyMediumName === name);
          return `
          <div style='display:flex;align-items:center;justify-content:center;' >
            <div style='width:12px;height:12px;background:linear-gradient(134deg, ${e.color.colorStops[0].color} 0%, ${
            e.color.colorStops[1].color
          } 100%);border-radius:50%;margin-right:8px'></div> 
            <div style='color: var(--mx-text-base-color);margin-right:15px;display:flex;justify-content:space-between;'><span>${
              e.data.name
            }</span> <span>${isNil(e.value) ? '--' : `${Number(e.value).toFixed(2)}kgce`}</span></div>
            <div style='color: var(--mx-text-base-color);' >${
              isNil(e.data.rate) ? '--' : Number(e.data.rate * 100).toFixed(2)
            }%</div>
          </div>
          `;
        },
      },
      legend: {
        orient: 'vertical',
        top: '244px',
        left: '10px',
        textStyle: {
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 22,
          width: 280,
          rich: {
            name: {
              // align: 'left',
              width: 110,
              color: 'var(--mx-text-secondary-color)',
              fontSize: 14,
            },
            b: {
              // align: 'right',
              width: 160,
              // padding: [0, 20, 0, 0],
              color: 'var(--mx-text-base-color)',
              fontSize: 14,
            },
            v: {
              // align: 'right',
              width: 30,
              color: 'var(--mx-text-base-color)',
              fontSize: 14,
            },
          },
        },
        formatter: (name: string) => {
          const item = data?.find(i => i.energyMediumName === name);
          return `{name|${name}} {b|${
            isNil(item) || isNil(item.standardCoalValue) ? '--' : `${item.standardCoalValue.toFixed(2)}kgce`
          }} {v|${isNil(item) || isNil(item.ratio) ? '--' : `${(item.ratio * 100).toFixed(2)}%`}}`;
        },
        data: data?.map(i => ({
          name: i.energyMediumName,
          icon: 'circle',
        })),
      },
      series: [
        {
          type: 'pie',
          top: '10px',
          left: '90px',
          radius: [70, 100],
          center: [98, 98],
          width: '200px',
          height: '200px',
          // avoidLabelOverlap: false, // 是否启用防止标签重叠策略，默认开启，在标签拥挤重叠的情况下会挪动各个标签的位置，防止标签间的重叠。
          labelLine: {
            // 标签的视觉引导线配置。在 label 位置 设置为'outside'的时候会显示视觉引导线。
            show: false,
          },
          label: {
            show: false,
            // position: 'center',
          },
          itemStyle: {
            // borderRadius: 2,
            borderColor: 'var(--mx-card-base-bg-color)',
            borderWidth: 2,
          },
          data: data?.map((i, index) => ({
            name: i.energyMediumName,
            value: i.standardCoalValue,
            rate: i.ratio,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: legendColors[index % legendColors.length]['0%'], // 0% 处的颜色
                  },
                  {
                    offset: 1,
                    color: legendColors[index % legendColors.length]['100%'], // 100% 处的颜色
                  },
                ],
                global: false, // 缺省为 false
              },
            },
          })),
          minAngle: 2,
          emphasis: {
            label: {
              show: false,
              fontSize: 10,
              fontWeight: 'bold',
            },
          },
          emptyCircleStyle: {
            color: 'var(--mx-base-border-color)',
          },
        },
      ],
    };
  };

  return (
    <EchartsWithTheme
      option={getChartOption()}
      theme={theme}
      style={{ height: '460px', width: '400px' }} // 样式的设置width与height
    />
  );
};

export default PieCharts;
